import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
    AdvanceFilter,
    AdvanceFilterFieldType,
    AdvanceFilterConfig,
    FilterTypeOperatorMap,
    AdvanceFilterOperator,
} from '@/types/types';
import { ChevronDown, ChevronUp, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DELIVERY_STATUS_OPTIONS, FILTER_OPERATOR_OPTIONS } from '@/utils/constant';
import { v4 as uuid } from 'uuid';
import { FormFieldSelect, Option } from '@/components/FormFieldSelect';
import { FormProvider, useForm } from 'react-hook-form';
import { AdvanceFilterValueField } from '@/components/AdvanceFilterComponents';
import { useTeamGetAll } from '@/services/teamService';

const deliveryFilterConfig: AdvanceFilterConfig[] = [
    {
        value: 'completedDate',
        label: 'Completed Date',
        fieldType: AdvanceFilterFieldType.DATE,
        inputType: 'DATE',
    },
    {
        value: 'createdOnDate',
        label: 'Created on Date',
        fieldType: AdvanceFilterFieldType.DATE,
        inputType: 'DATE',
    },
    {
        value: 'workOrderNumber',
        label: 'Work Order Number',
        fieldType: AdvanceFilterFieldType.ID,
        inputType: 'INPUT',
    },
    {
        value: 'pickupDate',
        label: 'Pick up Date',
        fieldType: AdvanceFilterFieldType.DATE,
        inputType: 'DATE',
    },
    {
        value: 'deliveryDate',
        label: 'Delivery Date',
        fieldType: AdvanceFilterFieldType.DATE,
        inputType: 'DATE',
    },
    {
        value: 'clientId',
        label: 'Client ID',
        fieldType: AdvanceFilterFieldType.ID,
        inputType: 'INPUT',
    },
    {
        value: 'driverName',
        label: "Driver's Name",
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'caseName',
        label: 'Case Name',
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'caseNumber',
        label: 'Case Number',
        fieldType: AdvanceFilterFieldType.ID,
        inputType: 'INPUT',
    },
    {
        value: 'fileNumber',
        label: 'File Number',
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'clientContact',
        label: "Client's Contact",
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'pickupAddress',
        label: 'Pickup Address',
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'deliveryAddress',
        label: 'Delivery Address',
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'deliveryPersonName',
        label: 'Delivery Person Name',
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'pickupPersonName',
        label: 'Pickup Person Name',
        fieldType: AdvanceFilterFieldType.STRING,
        inputType: 'INPUT',
    },
    {
        value: 'status',
        label: 'Status',
        fieldType: AdvanceFilterFieldType.ID,
        inputType: 'SELECT',
    },
    {
        value: 'teamId',
        label: 'Team',
        fieldType: AdvanceFilterFieldType.ID,
        inputType: 'SELECT',
    },
];

export const DeliveryAdvanceFilters = ({
    onClose,
    filters,
    setFilters,
    deliveryListViewFilters,
    open,
}: {
    onClose: () => void;
    filters: AdvanceFilter[];
    setFilters: (filters: AdvanceFilter[]) => void;
    deliveryListViewFilters: string[];
    open: boolean;
}) => {
    const form = useForm({
        mode: 'onChange',
    });

    const [fieldDefaultOptions, setFieldDefaultOptions] = useState<Record<string, Option[]>>({
        status: DELIVERY_STATUS_OPTIONS,
    });

    const { data: teams } = useTeamGetAll();

    useEffect(() => {
        if (teams?.teams) {
            setFieldDefaultOptions((prev) => ({
                ...prev,
                teamId: teams.teams.map((t) => ({ label: t.name, value: t.id })),
            }));
        }
    }, [teams]);

    const [selectedFilters, setSelectedFilter] = useState<
        {
            id: string;
            operators?: AdvanceFilterOperator[];
            filter?: AdvanceFilter;
            isExpanded?: boolean;
        }[]
    >([]);

    useEffect(() => {
        if (filters) {
            setSelectedFilter(
                filters
                    .filter((filter) => filter.field && !deliveryListViewFilters.includes(filter.field))
                    .map((filter) => ({
                        id: uuid(),
                        filter,
                        isExpanded: false,
                        operators: filter?.fieldType
                            ? deliveryFilterConfig.find((config) => config.value === filter.field)?.customFilterTypeOperator ??
                            FilterTypeOperatorMap[filter?.fieldType]
                            : [],
                    }))
            );
        }
    }, [filters]);

    const onDeleteFilter = (id: string) => setSelectedFilter((prev) => prev.filter((f) => f.id !== id));

    const toggleExpand = (id: string, expand: boolean) =>
        setSelectedFilter((prev) =>
            prev.map((f) =>
                f.id === id
                    ? {
                        ...f,
                        isExpanded: expand,
                    }
                    : f
            )
        );

    return (
        <Sheet modal={true} open={open}>
            <SheetContent
                hideCloseButton={true}
                className="w-[80vw] md:w-[500px] scrollbar-hide bg-white px-4 py-7 flex flex-col gap-6"
                side="right"
                tabIndex={-1}
            >
                <div className="flex w-full">
                    <div className="w-full">
                        <div className="flex justify-between">
                            <div className="text-[20px]">Advance Search</div>
                            <div className="border-[1px] p-[6px] rounded-md cursor-pointer" onClick={onClose}>
                                <X color="#676767" className="h-[20px] w-[20px]" />
                            </div>
                        </div>
                        <hr className="mt-4 border-[#CAC8C8]" />
                        <div className="mt-5 flex justify-between items-center">
                            <div className="flex gap-2 items-center">
                                <SlidersHorizontal className="w-5 h-5 text-primary" />
                                <span className="text-[16px] font-medium text-primary">Advance Search Filters </span>
                            </div>
                            {selectedFilters.length !== deliveryFilterConfig.length && (
                                <Button
                                    className="h-8 flex gap-2"
                                    onClick={() => {
                                        setSelectedFilter((prev) => [
                                            ...prev,
                                            {
                                                id: uuid(),
                                                isExpanded: true,
                                                operators: [],
                                            },
                                        ]);
                                    }}
                                >
                                    <Plus className="w-4 h-4" />
                                    <span className="text-[14px]">Add Filter</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <FormProvider {...form}>
                    <div className="flex-1 flex flex-col overflow-y-auto gap-6 custom-scrollbar">
                        {selectedFilters.map((selectedFilter) => {
                            const config = deliveryFilterConfig.find((config) => config.value === selectedFilter.filter?.field);
                            return (
                                <div
                                    key={selectedFilter.id}
                                    className={`p-3 flex flex-col gap-6 border-[1px] rounded-xl border-[#969696] relative ${selectedFilter.isExpanded ? 'cursor-default' : 'cursor-pointer'}`}
                                    onClick={() => !selectedFilter.isExpanded && toggleExpand(selectedFilter.id, true)}
                                >
                                    {selectedFilter.isExpanded ? (
                                        <>
                                            <div>
                                                <FormFieldSelect
                                                    options={deliveryFilterConfig.filter(
                                                        (config) =>
                                                            !selectedFilters.some(
                                                                (filter) =>
                                                                    filter.filter?.field === config.value &&
                                                                    filter.filter.field !== selectedFilter.filter?.field
                                                            )
                                                    )}
                                                    name={`${selectedFilter.id}_field`}
                                                    defaultValue={selectedFilter.filter?.field ?? ''}
                                                    label="Search Fields"
                                                    onChange={(newValue) => {
                                                        const value = newValue;
                                                        const filter = deliveryFilterConfig.find((config) => config.value === newValue);
                                                        if (filter) {
                                                            const operators =
                                                                filter?.customFilterTypeOperator ?? FilterTypeOperatorMap[filter?.fieldType] ?? [];
                                                            if (filter) {
                                                                setSelectedFilter((prev) =>
                                                                    prev.map((f) =>
                                                                        f.id === selectedFilter.id
                                                                            ? {
                                                                                ...f,
                                                                                operators: operators,
                                                                                filter: {
                                                                                    ...f.filter,
                                                                                    field: value,
                                                                                    value: '',
                                                                                    fieldType: filter.fieldType,
                                                                                    operator: operators[0],
                                                                                },
                                                                            }
                                                                            : f
                                                                    )
                                                                );
                                                            }
                                                        }
                                                    }}
                                                    placeholder="Select Search fields"
                                                />
                                            </div>
                                            {selectedFilter?.filter && (
                                                <>
                                                    <FormFieldSelect
                                                        options={FILTER_OPERATOR_OPTIONS.filter(
                                                            (operator) =>
                                                                selectedFilter.operators &&
                                                                selectedFilter.filter?.fieldType &&
                                                                FilterTypeOperatorMap[selectedFilter.filter?.fieldType].includes(operator.value)
                                                        )}
                                                        name={`${selectedFilter.id}_operator`}
                                                        defaultValue={selectedFilter.filter?.operator ?? ''}
                                                        label="Criteria"
                                                        placeholder="Select Criteria"
                                                        disabled={selectedFilter.operators?.length === 1}
                                                        onChange={(newValue) => {
                                                            const value = newValue;
                                                            setSelectedFilter((prev) =>
                                                                prev.map((f) =>
                                                                    f.id === selectedFilter.id
                                                                        ? {
                                                                            ...f,
                                                                            filter: {
                                                                                ...f.filter,
                                                                                operator: value as AdvanceFilterOperator,
                                                                            },
                                                                        }
                                                                        : f
                                                                )
                                                            );
                                                        }}
                                                    />
                                                    <AdvanceFilterValueField
                                                        inputType={
                                                            deliveryFilterConfig.find((config) => config.value === selectedFilter.filter?.field)
                                                                ?.inputType ?? 'INPUT'
                                                        }
                                                        name={`${selectedFilter.id}_value`}
                                                        value={selectedFilter.filter.value ?? ''}
                                                        options={
                                                            selectedFilter.filter.field
                                                                ? fieldDefaultOptions[selectedFilter.filter.field] ?? []
                                                                : []
                                                        }
                                                        onValueChange={(value) => {
                                                            setSelectedFilter((prev) =>
                                                                prev.map((f) =>
                                                                    f.id === selectedFilter.id
                                                                        ? {
                                                                            ...f,
                                                                            filter: {
                                                                                ...f.filter,
                                                                                value: value,
                                                                            },
                                                                        }
                                                                        : f
                                                                )
                                                            );
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex flex-col">
                                            <span className="text-sm">{config?.label}</span>
                                            <div className="text-[#969696]">
                                                <span className="text-sm">
                                                    {
                                                        FILTER_OPERATOR_OPTIONS.find(
                                                            (operator) => operator.value === selectedFilter.filter?.operator
                                                        )?.label
                                                    }
                                                </span>

                                                {selectedFilter.filter?.value && selectedFilter.filter.field && (
                                                    <span className="break-words">
                                                        &nbsp;•&nbsp;
                                                        {config?.inputType === 'INPUT'
                                                            ? selectedFilter.filter.value
                                                            : config?.inputType === 'SELECT'
                                                                ? fieldDefaultOptions?.[selectedFilter.filter.field]?.find(
                                                                    (option) => option.value === selectedFilter.filter?.value
                                                                )?.label
                                                                : ''}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="absolute top-3 right-3 cursor-pointer flex items-center gap-3">
                                        {selectedFilter.filter ? (
                                            !selectedFilter.isExpanded ? (
                                                <ChevronDown
                                                    className="h-5 w-5"
                                                    color="#969696"
                                                    onClick={() => toggleExpand(selectedFilter.id, true)}
                                                />
                                            ) : (
                                                <ChevronUp
                                                    className="h-5 w-5"
                                                    color="#969696"
                                                    onClick={() => toggleExpand(selectedFilter.id, false)}
                                                />
                                            )
                                        ) : null}
                                        <Trash2 color="#D72222" className="h-5 w-5" onClick={() => onDeleteFilter(selectedFilter.id)} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </FormProvider>

                <div className="bg-white sticky bottom-0">
                    <div className="flex justify-end gap-3">
                        {selectedFilters.length > 1 && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedFilter([]);
                                    setFilters(filters.filter((filter) => filter.field && deliveryListViewFilters.includes(filter.field)));
                                    onClose();
                                }}
                                className="px-4 py-3"
                            >
                                Clear All
                            </Button>
                        )}
                        <Button
                            className="px-7 py-3"
                            onClick={() => {
                                setFilters(
                                    selectedFilters
                                        .filter(
                                            (selectedFilter): selectedFilter is { id: string; filter: AdvanceFilter } =>
                                                !!selectedFilter.filter && !!selectedFilter.filter.value
                                        )
                                        .map((selectedFilter) => selectedFilter.filter)
                                        .concat(filters.filter((filter) => filter.field && deliveryListViewFilters.includes(filter.field)))
                                );
                                onClose();
                            }}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
